import * as XLSX from 'xlsx';
import { deriveCardholderName, deriveCardEnding } from '../../utils/cardholder';
import { formatFlagForExport } from '../../utils/flagExportLabel';
import { excelDateCell, excelDateTimeCell } from '../../utils/excel';
import { applyExcelPrintSetup } from '../../utils/xlsxPrintSetup';
import { formatPercentage, getClientName, getSpouseName, getClientAge, getSpouseAge, formatOwnershipAllocation, formatEntityName, collectAbbreviationLegend, collectTitleAllocations } from '../../utils/formatters';

const CURRENCY_FMT = '$#,##0.00';

// Build a real Excel date cell — a numeric serial carrying the M/D/YYYY number
// format — returning '' for empty/missing values so blank cells stay blank
// instead of rendering "NaN/NaN/NaN". Every date written to the workbook goes
// through here, so the report never leaks ISO strings and never writes a date
// as text (which Excel refuses to sort or use in date functions).
function usDate(value) {
  return excelDateCell(value);
}

// Ownership/source allocation → "Name (pct%)" joined list, e.g.
// "J (50.0%), Jane Doe (50.0%)". Mirrors the Source column in the
// TransactionViewer table. Abbreviates where the entity has a short form set
// (per the 2026-07-21 export decision); the abbreviation legend at the top of
// the sheet maps each short form back to the full legal name.
function formatSourceList(source) {
  if (!Array.isArray(source) || source.length === 0) return '';
  return source
    .map(s => {
      const name = formatEntityName(s, { abbreviate: true });
      const pct = s?.pct;
      return name && pct !== undefined && pct !== null ? `${name} (${formatPercentage(pct)})` : name;
    })
    .filter(Boolean)
    .join(', ');
}

const headerFill = { fgColor: { rgb: '334E68' } };
const headerFont = { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 };
const titleFont = { bold: true, sz: 14 };
const subtitleFont = { bold: true, sz: 12 };

function applyHeaderStyle(ws, range) {
  if (!ws['!cols']) ws['!cols'] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    const addr = XLSX.utils.encode_cell({ r: range.s.r, c: C });
    if (!ws[addr]) continue;
    ws[addr].s = { fill: headerFill, font: headerFont, alignment: { horizontal: 'center', wrapText: true } };
  }
}

function setColWidths(ws, widths) {
  ws['!cols'] = widths.map(w => ({ wch: w }));
}

function addCell(ws, r, c, value, style) {
  const addr = XLSX.utils.encode_cell({ r, c });
  // Pre-built cell objects (the date cells from usDate) are written through
  // as-is — SheetJS accepts the same shape inside an AOA.
  const isCellObject = value !== null && typeof value === 'object' && !(value instanceof Date);
  const cell = isCellObject ? { ...value } : { v: value };
  if (style) cell.s = style;
  if (typeof value === 'number' && style?.z) {
    cell.t = 'n';
    cell.z = style.z;
  }
  ws[addr] = cell;
  const ref = ws['!ref'];
  const newRef = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: Math.max(r, ref ? XLSX.utils.decode_range(ref).e.r : 0), c: Math.max(c, ref ? XLSX.utils.decode_range(ref).e.c : 0) }
  });
  ws['!ref'] = newRef;
}

// ────────────────────────────────────────────────
// Title Sheet
// ────────────────────────────────────────────────
function buildTitleSheet(caseDetails, reportForClient) {
  const ws = XLSX.utils.aoa_to_sheet([]);
  const clientName = reportForClient ? getClientName(caseDetails) : getSpouseName(caseDetails);
  const spouseName = reportForClient ? getSpouseName(caseDetails) : getClientName(caseDetails);

  const rows = [
    ['Estate Summary Export'],
    [],
    ['Case Information'],
    ['Case Number', caseDetails?.caseNumber || ''],
    ['Court', caseDetails?.courtName || ''],
    ['Jurisdiction', caseDetails?.jurisdictionState || ''],
    [],
    ['Parties'],
    ['Petitioner', clientName],
    ['Respondent', spouseName],
    [],
    ['Key Dates'],
    ['Date of Marriage', usDate(caseDetails?.marriageDate)],
    ['Date of Separation', usDate(caseDetails?.separationDate)],
    ['Date of Filing', usDate(caseDetails?.filingDate)],
    [],
    ['Export Date', usDate(new Date())],
  ];

  XLSX.utils.sheet_add_aoa(ws, rows, { origin: 'A1' });

  addCell(ws, 0, 0, 'Estate Summary Export', { font: titleFont });
  addCell(ws, 2, 0, 'Case Information', { font: subtitleFont });
  addCell(ws, 7, 0, 'Parties', { font: subtitleFont });
  addCell(ws, 11, 0, 'Key Dates', { font: subtitleFont });

  setColWidths(ws, [22, 35]);
  return ws;
}

// ────────────────────────────────────────────────
// Personal Info Sheet
// ────────────────────────────────────────────────
function buildPersonalInfoSheet(caseDetails, reportForClient) {
  const ws = XLSX.utils.aoa_to_sheet([]);
  let row = 0;

  const clientLabel = reportForClient ? 'Client' : 'Spouse';
  const spouseLabel = reportForClient ? 'Spouse' : 'Client';
  const clientName = reportForClient ? getClientName(caseDetails) : getSpouseName(caseDetails);
  const spouseName = reportForClient ? getSpouseName(caseDetails) : getClientName(caseDetails);
  const clientAge = reportForClient ? getClientAge(caseDetails) : getSpouseAge(caseDetails);
  const spouseAge = reportForClient ? getSpouseAge(caseDetails) : getClientAge(caseDetails);

  const petitioner = caseDetails?.petitioner || {};
  const respondent = caseDetails?.respondent || {};

  const clientParty = reportForClient ? petitioner : respondent;
  const spouseParty = reportForClient ? respondent : petitioner;

  const partyRows = [
    ['Party Details'],
    [],
    ['', clientLabel, spouseLabel],
    ['Full Name', clientName, spouseName],
    ['Date of Birth', usDate(clientParty.dateOfBirth || clientParty.dob || clientParty.birthDate || ''), usDate(spouseParty.dateOfBirth || spouseParty.dob || spouseParty.birthDate || '')],
    ['Age', clientAge || '', spouseAge || ''],
    ['Role', clientParty.case_role || clientParty.assigned_role || '', spouseParty.case_role || spouseParty.assigned_role || ''],
  ];
  XLSX.utils.sheet_add_aoa(ws, partyRows, { origin: 'A1' });
  addCell(ws, 0, 0, 'Party Details', { font: subtitleFont });
  addCell(ws, 2, 1, clientLabel, { font: { bold: true } });
  addCell(ws, 2, 2, spouseLabel, { font: { bold: true } });
  row = partyRows.length;

  const children = caseDetails?.children || [];
  if (children.length > 0) {
    row += 1;
    addCell(ws, row, 0, 'Children', { font: subtitleFont });
    row += 1;
    const childHeaders = ['Name', 'Date of Birth', 'Age'];
    childHeaders.forEach((h, c) => addCell(ws, row, c, h, { font: { bold: true } }));
    row += 1;

    children.forEach(child => {
      const dob = child.dateOfBirth || child.dob || child.birthDate || '';
      let age = '';
      if (dob) {
        const birthDate = new Date(dob);
        const today = new Date();
        age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
      }
      const fullName = child.fullName || `${child.firstName || ''} ${child.lastName || ''}`.trim();
      addCell(ws, row, 0, fullName);
      addCell(ws, row, 1, usDate(dob));
      addCell(ws, row, 2, age);
      row += 1;
    });
  }

  const addresses = caseDetails?.addresses || [];
  if (addresses.length > 0) {
    row += 1;
    addCell(ws, row, 0, 'Known Addresses', { font: subtitleFont });
    row += 1;
    const addrHeaders = ['Address', 'Relevance', 'Label'];
    addrHeaders.forEach((h, c) => addCell(ws, row, c, h, { font: { bold: true } }));
    row += 1;

    addresses.forEach(addr => {
      const relevanceLabel = (addr.relevance || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      addCell(ws, row, 0, addr.address || '');
      addCell(ws, row, 1, relevanceLabel);
      addCell(ws, row, 2, addr.label || '');
      row += 1;
    });
  }

  setColWidths(ws, [22, 30, 30]);
  return ws;
}

// ────────────────────────────────────────────────
// Income / Expense Sheet (shared builder)
// ────────────────────────────────────────────────
function buildIncomeExpenseSheet(drfaData, type, displayOptions, caseDetails) {
  const items = (drfaData || []).filter(item => {
    const itemType = (item.type || item.category || '').toLowerCase();
    return type === 'income' ? itemType.includes('income') : itemType.includes('expense');
  });

  const headers = [
    'Category',
    'Subcategory',
    'Stated Amount',
    'Transaction Count',
    'Total Amount',
    'Monthly Average',
    'Earliest Transaction',
    'Latest Transaction',
  ];

  const rows = [headers];

  let totalStated = 0;
  let totalAmount = 0;
  let totalMonthlyAvg = 0;

  items.forEach(item => {
    const stated = item.stated_amount || 0;
    const total = item.total_amount || 0;
    const monthlyAvg = item.monthly_average_amount || 0;
    totalStated += stated;
    totalAmount += total;
    totalMonthlyAvg += monthlyAvg;

    rows.push([
      item.category || '',
      item.subcategory || item.drfa_category_name || '',
      stated,
      item.transaction_count || 0,
      total,
      monthlyAvg,
      usDate(item.earliest_transaction),
      usDate(item.latest_transaction),
    ]);
  });

  rows.push([]);
  rows.push([
    'TOTAL', '',
    totalStated,
    '',
    totalAmount,
    totalMonthlyAvg,
    '', '',
  ]);

  if (displayOptions.showSupportFields && type === 'income') {
    rows.push([]);
    rows.push(['Spousal Support (Monthly)', '', caseDetails?.monthlySpousalSupport || 0]);
    rows.push(['Child Support (Monthly)', '', caseDetails?.monthlyChildSupport || 0]);
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Apply currency formatting to amount columns (cols 2,4,5)
  const dataRange = XLSX.utils.decode_range(ws['!ref']);
  for (let R = 1; R <= dataRange.e.r; R++) {
    [2, 4, 5].forEach(C => {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      if (ws[addr] && typeof ws[addr].v === 'number') {
        ws[addr].z = CURRENCY_FMT;
      }
    });
  }

  applyHeaderStyle(ws, { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } });
  setColWidths(ws, [20, 28, 16, 16, 16, 16, 18, 18]);
  return ws;
}

// ────────────────────────────────────────────────
// Asset / Liability Sheet (shared builder)
// ────────────────────────────────────────────────
// Mirrors calculateSourceValues from table-asset-liability.js
function calculateSourceValues(account) {
  const end_balance = account.latest_balance?.end_balance || 0;
  const petitionerSource = account._petitioner_owning_pct || 0;
  const petitionerValue = account.latest_balance?._petitioner_owning_value ?? end_balance * petitionerSource;
  const respondentSource = account._respondent_owning_pct || 0;
  const respondentValue = account.latest_balance?._respondent_owning_value ?? end_balance * respondentSource;
  const otherSource = account._stated_other_pct || 0;
  const otherValue = end_balance * otherSource;
  const maritalSource = account._stated_marital_pct ?? (1 - petitionerSource - respondentSource - otherSource);
  const petitionerSettlement = account._petitioner_settlement_pct || 0;
  const maritalValue = end_balance * maritalSource;
  const petitionerSettlementValue = account.latest_balance?._petitioner_settlement_value ?? maritalValue * petitionerSettlement;
  const respondentSettlementValue = maritalValue - petitionerSettlementValue;

  return {
    petitionerValue, respondentValue, otherValue, maritalValue,
    petitionerSource, respondentSource, otherSource, maritalSource,
    petitionerSettlement, respondentSettlement: 1 - petitionerSettlement,
    petitionerSettlementValue, respondentSettlementValue,
  };
}

// Mirrors calculateAccountNetValue from table-asset-liability.js
function calculateAccountNetValue(account) {
  let netValue = account.latest_balance?.end_balance || 0;
  if (account.associated_liabilities) {
    netValue += account.associated_liabilities.reduce(
      (sum, liab) => sum + (liab.latest_balance?.end_balance || 0), 0
    );
  }
  return netValue;
}

// Mirrors calculateEquitySourceValues from table-asset-liability.js
// Computes source/settlement totals including associated liabilities using parent's percentages
function calculateEquitySourceValues(account) {
  const sv = calculateSourceValues(account);
  let totalPetitionerValue = sv.petitionerValue;
  let totalRespondentValue = sv.respondentValue;
  let totalOtherValue = sv.otherValue;
  let totalMaritalValue = sv.maritalValue;
  let totalPetitionerSettlementValue = sv.petitionerSettlementValue;
  let totalRespondentSettlementValue = sv.respondentSettlementValue;

  if (account.associated_liabilities) {
    account.associated_liabilities.forEach(liability => {
      const liabilityValue = liability.latest_balance?.end_balance || 0;
      totalPetitionerValue += liabilityValue * sv.petitionerSource;
      totalRespondentValue += liabilityValue * sv.respondentSource;
      totalOtherValue += liabilityValue * sv.otherSource;
      const incrementalMaritalValue = liabilityValue * sv.maritalSource;
      totalMaritalValue += incrementalMaritalValue;
      const incrementalPetitionerSettlement = incrementalMaritalValue * sv.petitionerSettlement;
      totalPetitionerSettlementValue += incrementalPetitionerSettlement;
      totalRespondentSettlementValue += incrementalMaritalValue - incrementalPetitionerSettlement;
    });
  }

  return {
    petitionerValue: totalPetitionerValue,
    respondentValue: totalRespondentValue,
    otherValue: totalOtherValue,
    maritalValue: totalMaritalValue,
    petitionerSettlementValue: totalPetitionerSettlementValue,
    respondentSettlementValue: totalRespondentSettlementValue,
  };
}

// Mirrors calculateSectionSourceTotals from table-asset-liability.js
function calculateSectionSourceTotals(accounts) {
  let petitionerTotal = 0;
  let respondentTotal = 0;
  let otherTotal = 0;
  let maritalTotal = 0;
  let petitionerSettlementTotal = 0;
  let respondentSettlementTotal = 0;

  accounts.forEach(account => {
    const sv = calculateSourceValues(account);
    petitionerTotal += sv.petitionerValue;
    respondentTotal += sv.respondentValue;
    otherTotal += sv.otherValue;
    maritalTotal += sv.maritalValue;
    petitionerSettlementTotal += sv.petitionerSettlementValue;
    respondentSettlementTotal += sv.respondentSettlementValue;

    if (account.associated_liabilities) {
      account.associated_liabilities.forEach(liability => {
        const liabilityValue = liability.latest_balance?.end_balance || 0;
        petitionerTotal += liabilityValue * sv.petitionerSource;
        respondentTotal += liabilityValue * sv.respondentSource;
        otherTotal += liabilityValue * sv.otherSource;
        const incrementalMaritalValue = liabilityValue * sv.maritalSource;
        maritalTotal += incrementalMaritalValue;
        const incrementalPetitionerSettlement = incrementalMaritalValue * sv.petitionerSettlement;
        petitionerSettlementTotal += incrementalPetitionerSettlement;
        respondentSettlementTotal += incrementalMaritalValue - incrementalPetitionerSettlement;
      });
    }
  });

  return { petitionerTotal, respondentTotal, otherTotal, maritalTotal, petitionerSettlementTotal, respondentSettlementTotal };
}

function buildAssetLiabilitySheet(data, category, displayOptions) {
  const sections = data?.[category] || {};

  // Column structure matches DRFA report: #, Account, Title, Source, Date, Value, [source cols], [settlement cols], Notes
  const headers = ['#', 'Account', 'Title', 'Source', 'Date', 'Value'];
  if (displayOptions.showSourceColumns) {
    headers.push('Petitioner Source', 'Respondent Source', 'Other Source', 'Marital Source');
  }
  if (displayOptions.showSettlementColumns) {
    headers.push('Petitioner Settlement', 'Respondent Settlement');
  }
  if (displayOptions.showNotes) headers.push('Notes');

  const rows = [headers];
  const valueCol = 5;

  let lineNumber = 1;
  let grandTotal = 0;
  const grandSourceTotals = { petitionerTotal: 0, respondentTotal: 0, otherTotal: 0, maritalTotal: 0, petitionerSettlementTotal: 0, respondentSettlementTotal: 0 };

  Object.entries(sections).forEach(([sectionName, section]) => {
    const accounts = section.accounts || [];
    let sectionTotal = 0;

    // Section header row (merged label)
    rows.push([sectionName]);

    accounts.forEach(account => {
      const balance = account.latest_balance?.end_balance || 0;
      const sv = calculateSourceValues(account);

      // Account row
      const row = [
        lineNumber++,
        account._displayName || account.account_name || account.name || '',
        (formatOwnershipAllocation(account.title, { abbreviate: true }) || account.account_holder || ''),
        account.latest_balance?._source || '',
        usDate(account.latest_balance?.end_date || account.latest_balance?.statement_date || ''),
        balance,
      ];
      if (displayOptions.showSourceColumns) {
        row.push(sv.petitionerValue, sv.respondentValue, sv.otherValue, sv.maritalValue);
      }
      if (displayOptions.showSettlementColumns) {
        row.push(sv.petitionerSettlementValue, sv.respondentSettlementValue);
      }
      if (displayOptions.showNotes) row.push(account.notes || account._notes || '');
      rows.push(row);

      // Associated liability rows — use parent account's percentages (same as DRFA report)
      (account.associated_liabilities || []).forEach(liab => {
        const liabValue = liab.latest_balance?.end_balance || 0;
        const liabPetitionerValue = liabValue * sv.petitionerSource;
        const liabRespondentValue = liabValue * sv.respondentSource;
        const liabMaritalValue = liabValue * sv.maritalSource;
        const liabPetitionerSettlement = liabMaritalValue * sv.petitionerSettlement;
        const liabRespondentSettlement = liabMaritalValue - liabPetitionerSettlement;

        const liabRow = [
          lineNumber++,
          `  ↳ ${liab._displayName || liab.account_name || liab.name || 'Liability'}`,
          (formatOwnershipAllocation(liab.title, { abbreviate: true }) || liab.account_holder || ''),
          liab.latest_balance?._source || '',
          usDate(liab.latest_balance?.end_date || liab.latest_balance?.statement_date || ''),
          liabValue,
        ];
        if (displayOptions.showSourceColumns) {
          liabRow.push(liabPetitionerValue, liabRespondentValue, liabMaritalValue);
        }
        if (displayOptions.showSettlementColumns) {
          liabRow.push(liabPetitionerSettlement, liabRespondentSettlement);
        }
        if (displayOptions.showNotes) liabRow.push('');
        rows.push(liabRow);
      });

      // Net Equity row for assets with associated liabilities (same as DRFA report)
      if (category === 'assets' && account.associated_liabilities && account.associated_liabilities.length > 0) {
        const netValue = calculateAccountNetValue(account);
        const eqSv = calculateEquitySourceValues(account);

        const eqRow = [
          lineNumber++,
          `  ${account._displayName || account.account_name || ''} Net Equity`,
          '', '', '',
          netValue,
        ];
        if (displayOptions.showSourceColumns) {
          eqRow.push(eqSv.petitionerValue, eqSv.respondentValue, eqSv.otherValue, eqSv.maritalValue);
        }
        if (displayOptions.showSettlementColumns) {
          eqRow.push(eqSv.petitionerSettlementValue, eqSv.respondentSettlementValue);
        }
        if (displayOptions.showNotes) eqRow.push('');
        rows.push(eqRow);
      }

      // Accumulate section total using net value (same as DRFA's calculateSectionTotal)
      sectionTotal += calculateAccountNetValue(account);
    });

    // Section source totals (includes liability adjustments via parent percentages)
    const sectionSourceTotals = calculateSectionSourceTotals(accounts);

    // Section total row
    const totalRow = [
      lineNumber++,
      `Total ${sectionName}`,
      '', '', '',
      sectionTotal,
    ];
    if (displayOptions.showSourceColumns) {
      totalRow.push(sectionSourceTotals.petitionerTotal, sectionSourceTotals.respondentTotal, sectionSourceTotals.otherTotal, sectionSourceTotals.maritalTotal);
    }
    if (displayOptions.showSettlementColumns) {
      totalRow.push(sectionSourceTotals.petitionerSettlementTotal, sectionSourceTotals.respondentSettlementTotal);
    }
    if (displayOptions.showNotes) totalRow.push('');
    rows.push(totalRow);
    rows.push([]); // blank separator

    grandTotal += sectionTotal;
    grandSourceTotals.petitionerTotal += sectionSourceTotals.petitionerTotal;
    grandSourceTotals.respondentTotal += sectionSourceTotals.respondentTotal;
    grandSourceTotals.otherTotal += sectionSourceTotals.otherTotal;
    grandSourceTotals.maritalTotal += sectionSourceTotals.maritalTotal;
    grandSourceTotals.petitionerSettlementTotal += sectionSourceTotals.petitionerSettlementTotal;
    grandSourceTotals.respondentSettlementTotal += sectionSourceTotals.respondentSettlementTotal;
  });

  // Grand total row
  const grandRow = [
    lineNumber,
    `Total ${category === 'assets' ? 'Assets' : 'Liabilities'}`,
    '', '', '',
    grandTotal,
  ];
  if (displayOptions.showSourceColumns) {
    grandRow.push(grandSourceTotals.petitionerTotal, grandSourceTotals.respondentTotal, grandSourceTotals.otherTotal, grandSourceTotals.maritalTotal);
  }
  if (displayOptions.showSettlementColumns) {
    grandRow.push(grandSourceTotals.petitionerSettlementTotal, grandSourceTotals.respondentSettlementTotal);
  }
  if (displayOptions.showNotes) grandRow.push('');
  rows.push(grandRow);

  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Apply currency formatting to Value and source/settlement columns
  const dataRange = XLSX.utils.decode_range(ws['!ref']);
  const currencyCols = [valueCol];

  if (displayOptions.showSourceColumns) {
    const petSrcCol = headers.indexOf('Petitioner Source');
    currencyCols.push(petSrcCol, petSrcCol + 1, petSrcCol + 2, petSrcCol + 3);
  }
  if (displayOptions.showSettlementColumns) {
    const petSetCol = headers.indexOf('Petitioner Settlement');
    currencyCols.push(petSetCol, petSetCol + 1);
  }

  for (let R = 1; R <= dataRange.e.r; R++) {
    currencyCols.forEach(C => {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      if (ws[addr] && typeof ws[addr].v === 'number') {
        ws[addr].z = CURRENCY_FMT;
      }
    });
  }

  applyHeaderStyle(ws, { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } });

  const colWidths = [5, 32, 22, 14, 14, 16];
  if (displayOptions.showSourceColumns) colWidths.push(18, 18, 18);
  if (displayOptions.showSettlementColumns) colWidths.push(20, 20);
  if (displayOptions.showNotes) colWidths.push(25);
  setColWidths(ws, colWidths);

  return ws;
}

// ────────────────────────────────────────────────
// Transaction List Sheet
// ────────────────────────────────────────────────
// Modeled on the transaction table in the Sankey/Transaction Analysis panel
// (TransactionViewer). Each row is one enriched transaction. When the export
// is launched from the analysis panel the caller passes the currently
// filtered/sorted set; the Estate Summary Export page passes the full case
// feed. The Account column carries the source account's display name.
//
// Optional columns follow the same display flags the analysis panel uses
// (showMerchant / showSubcategory / showFlag / showSource); each defaults to
// on so callers that don't set them (e.g. the estate page) get all columns.
// showCardholder is the exception: it defaults OFF, because the caller decides
// whether any row is on a card (the analysis panel's hasCardRows gate) and a
// sheet must match the screen it came from.
function buildTransactionListSheet(transactions, options = {}) {
  const {
    showMerchant = true,
    showSubcategory = true,
    showFlag = true,
    showSource = true,
    showCardholder = false,
  } = options;

  // Cardholder sits right after Account, as on screen; Card Ending is its own
  // column so a recipient can filter one card in Excel.
  const headers = ['Date', 'Account'];
  if (showCardholder) headers.push('Cardholder', 'Card Ending');
  headers.push('Description', 'Amount', 'Category');
  if (showSubcategory) headers.push('Subcategory');
  if (showMerchant) headers.push('Merchant');
  if (showFlag) headers.push('Flag');
  if (showSource) headers.push('Source');

  const amountCol = showCardholder ? 5 : 3;
  const rows = [headers];
  let totalAmount = 0;

  (transactions || []).forEach(txn => {
    const t = txn.transaction || {};
    const amount = t.amount || 0;
    totalAmount += amount;

    const row = [
      usDate(t.posting_date),
      txn.bankAccount?._displayName || 'Unknown Account',
    ];
    if (showCardholder) row.push(deriveCardholderName(txn.card_view), deriveCardEnding(txn.card_view) || '');
    row.push(t.description || '', amount, txn.category?.name || 'Uncategorized');
    if (showSubcategory) row.push(txn.subcategory?.name || 'Uncategorized');
    if (showMerchant) row.push(txn.merchant?.name || '');
    if (showFlag) row.push(formatFlagForExport(txn) || '');
    if (showSource) row.push(formatSourceList(txn.source));
    rows.push(row);
  });

  rows.push([]);
  const totalRow = showCardholder ? ['', '', '', '', 'TOTAL', totalAmount] : ['', '', 'TOTAL', totalAmount];
  rows.push(totalRow);

  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Currency-format the Amount column
  const dataRange = XLSX.utils.decode_range(ws['!ref']);
  for (let R = 1; R <= dataRange.e.r; R++) {
    const addr = XLSX.utils.encode_cell({ r: R, c: amountCol });
    if (ws[addr] && typeof ws[addr].v === 'number') {
      ws[addr].z = CURRENCY_FMT;
    }
  }

  applyHeaderStyle(ws, { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } });

  const colWidths = showCardholder ? [14, 26, 24, 12, 40, 16, 20] : [14, 26, 40, 16, 20];
  if (showSubcategory) colWidths.push(20);
  if (showMerchant) colWidths.push(24);
  if (showFlag) colWidths.push(16);
  if (showSource) colWidths.push(28);
  setColWidths(ws, colWidths);

  // Filter dropdowns on the header, so an accountant slices and re-sorts this
  // in Excel natively instead of us approximating Excel's behavior.
  //
  // The range stops at the LAST DATA ROW — deliberately excluding the blank
  // separator and the TOTAL row pushed above. A range covering TOTAL lets an
  // Excel-side re-sort drag it into the middle of the data, and a filter hide
  // it. (The blank row also stops Excel's current-region expansion, so this is
  // belt and braces.)
  const dataRowCount = (transactions || []).length;
  if (dataRowCount > 0) {
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: dataRowCount, c: headers.length - 1 },
      }),
    };
  }

  // Narrow margins are worth having on their own, and they are also the anchor
  // applyExcelPrintSetup needs: OOXML requires pageSetup to follow pageMargins,
  // and without this SheetJS emits no pageMargins at all, so the patcher would
  // skip the landscape/fit-to-width setup rather than risk a corrupt file.
  ws['!margins'] = { left: 0.25, right: 0.25, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 };

  return ws;
}

// ────────────────────────────────────────────────
// Public: build the full workbook
// ────────────────────────────────────────────────
// "Abbreviations" sheet — maps each user-defined short form used in the Title
// (assets/liabilities) or Source (transactions) columns back to its full legal
// name. Spreadsheets have no hover tooltip, so this keeps an abbreviated cell
// traceable on a court-facing export. Returns null when no abbreviation is used.
function buildAbbreviationLegendSheet(assetLiabilityData, transactions) {
  const lists = collectTitleAllocations(assetLiabilityData);
  (transactions || []).forEach((txn) => {
    if (Array.isArray(txn?.source)) lists.push(txn.source);
  });
  const legend = collectAbbreviationLegend(lists);
  if (!legend.length) return null;

  const rows = [['Abbreviations'], ['Abbreviation', 'Full name']];
  legend.forEach(({ abbreviation, display_name }) => rows.push([abbreviation, display_name]));
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 16 }, { wch: 40 }];
  return ws;
}

// "Report Info" sheet — provenance for an export launched from the analysis
// panel: which filters narrowed the set, and what order the rows are in.
//
// The PDF prints both as captions under its title. A spreadsheet has nowhere to
// put a caption without pushing the data grid down, which breaks any formula,
// sort or AutoFilter the recipient builds assuming row 1 is the header. So it
// gets its own sheet and the Transactions grid stays a clean header-plus-rows
// block.
//
// This matters more in a spreadsheet than in the PDF: the recipient can re-sort
// the sheet themselves and destroy a deliberate ordering without ever knowing
// it was deliberate. Returns null when there is no provenance to record.
function buildReportInfoSheet({ sortDescription, filtersDescription, rowCount } = {}) {
  // describeActiveFilters() emits an array; the initial state is "" until the
  // first filter run, so tolerate both.
  const filters = Array.isArray(filtersDescription)
    ? filtersDescription
    : (filtersDescription ? [filtersDescription] : []);

  if (!sortDescription && !filters.length) return null;

  const rows = [['Report Info'], []];
  // Date AND time: re-exporting the same day is routine, and two files that
  // both say only "9/10/2026" cannot be told apart.
  rows.push(['Exported', excelDateTimeCell(new Date())]);
  if (typeof rowCount === 'number') rows.push(['Transactions', rowCount]);
  if (sortDescription) rows.push(['Sort order', sortDescription]);

  if (filters.length) {
    rows.push([]);
    rows.push(['Filters applied']);
    filters.forEach((desc) => rows.push(['', desc]));
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 18 }, { wch: 70 }];
  return ws;
}

export function buildEstateWorkbook({ caseDetails, drfaData, assetLiabilityData, transactions, displayOptions, reportForClient, selectedSheets, sortDescription, filtersDescription }) {
  const wb = XLSX.utils.book_new();
  const include = selectedSheets || new Set(['Title', 'Personal Info', 'Income', 'Expenses', 'Assets', 'Liabilities']);

  if (include.has('Title')) {
    XLSX.utils.book_append_sheet(wb, buildTitleSheet(caseDetails, reportForClient), 'Title');
  }
  if (include.has('Personal Info')) {
    XLSX.utils.book_append_sheet(wb, buildPersonalInfoSheet(caseDetails, reportForClient), 'Personal Info');
  }
  if (include.has('Income')) {
    XLSX.utils.book_append_sheet(wb, buildIncomeExpenseSheet(drfaData, 'income', displayOptions, caseDetails), 'Income');
  }
  if (include.has('Expenses')) {
    XLSX.utils.book_append_sheet(wb, buildIncomeExpenseSheet(drfaData, 'expense', displayOptions, caseDetails), 'Expenses');
  }
  if (include.has('Assets')) {
    XLSX.utils.book_append_sheet(wb, buildAssetLiabilitySheet(assetLiabilityData, 'assets', displayOptions), 'Assets');
  }
  if (include.has('Liabilities')) {
    XLSX.utils.book_append_sheet(wb, buildAssetLiabilitySheet(assetLiabilityData, 'liabilities', displayOptions), 'Liabilities');
  }
  if (include.has('Transactions')) {
    XLSX.utils.book_append_sheet(wb, buildTransactionListSheet(transactions, displayOptions), 'Transactions');
  }

  // Legend last — only when a short form is actually used in this workbook.
  const legendSheet = buildAbbreviationLegendSheet(assetLiabilityData, transactions);
  if (legendSheet) XLSX.utils.book_append_sheet(wb, legendSheet, 'Abbreviations');

  // Provenance after the legend so the data sheet stays the one Excel opens on.
  const infoSheet = buildReportInfoSheet({
    sortDescription,
    filtersDescription,
    rowCount: Array.isArray(transactions) ? transactions.length : undefined,
  });
  if (infoSheet) XLSX.utils.book_append_sheet(wb, infoSheet, 'Report Info');

  return wb;
}

// Print setup for a transactions export. Nine columns run ~16.5in wide against
// ~7in of portrait printable width, so Excel's defaults split them into three
// separate page runs with no repeated header — which reads as an unsorted
// printout even when the row order is correct.
export const TRANSACTION_SHEET_PRINT_SETUP = [{
  sheet: 'Transactions',
  orientation: 'landscape',
  fitToWidth: 1,
  freezeRows: 1,
  printTitleRows: '1:1',
}];

export function getDefaultFileName(caseDetails) {
  const caseNum = caseDetails?.caseNumber || 'Case';
  return `${caseNum}_Estate_Summary_${todayStamp()}.xlsx`;
}

// Filename for a transactions-only export launched from the analysis panel.
export function getTransactionListFileName(caseDetails) {
  const caseNum = caseDetails?.caseNumber || 'Case';
  return `${caseNum}_Transactions_${todayStamp()}.xlsx`;
}

function todayStamp() {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
}

// Write a workbook to a file. Uses the File System Access API's save picker
// when available (lets the user choose the location), otherwise falls back to
// a synthetic anchor download. Shared by the Estate Summary Export page and
// the transaction analysis panel. Swallows the AbortError raised when the
// user cancels the save picker.
export async function downloadWorkbook(wb, fileName, printSetup = null) {
  // type:'array' hands back an ArrayBuffer directly — no charCode transcription
  // loop — and is what applyExcelPrintSetup expects.
  let bytes = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

  // Bake in page setup that SheetJS cannot express (landscape, fit-to-width,
  // repeated header row, frozen pane). Only sheets named in printSetup are
  // touched, so a caller that passes nothing gets a byte-identical workbook.
  if (printSetup?.length) {
    bytes = applyExcelPrintSetup(bytes, printSetup);
  }

  const blob = new Blob([bytes], { type: 'application/octet-stream' });

  if (window.showSaveFilePicker) {
    const handle = await window.showSaveFilePicker({
      suggestedName: fileName,
      types: [{
        description: 'Excel Files',
        accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
      }],
    });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
  } else {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
